import { useId, useRef, type ReactNode } from 'react';
import { cn } from './cn.js';

/// Um campo de código com o texto colorido por baixo.
///
/// A cor não é enfeite: num corpo de e-mail o que importa enxergar de relance é onde estão as
/// variáveis — `{{firstName}}` some no meio de `<p style="...">` quando tudo tem a mesma cor, e
/// variável no lugar errado é o erro que só aparece na caixa de entrada de alguém.
///
/// É um `textarea` de verdade, transparente, sobre um `<pre>` pintado. Seleção, desfazer, corretor
/// e leitor de tela continuam sendo os do navegador; o que muda é só o que se vê. Editor
/// reimplementado em `contenteditable` daria a cor de graça e cobraria o resto.
export interface CodeEditorProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  /// Linhas com problema, para o campo mostrar onde doeu em vez de só dizer que dói.
  errorLines?: readonly number[];
  hint?: string | undefined;
  rows?: number;
  id?: string;
  toolbar?: ReactNode;
  /// Quando quem está olhando não pode editar. O texto continua legível e selecionável — esconder o
  /// conteúdo de quem tem permissão de ler seria o oposto do que a permissão diz.
  readOnly?: boolean;
  className?: string;
}

const SHARED_TEXT =
  'm-0 w-full whitespace-pre-wrap break-words border-0 p-4 font-mono text-[13px] leading-6';

export function CodeEditor({
  label,
  value,
  onValueChange,
  errorLines = [],
  hint,
  rows = 16,
  id: providedId,
  toolbar,
  readOnly = false,
  className,
}: CodeEditorProps) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const highlight = useRef<HTMLDivElement>(null);

  return (
    <div className="space-y-2">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="block text-xs font-semibold text-content/70">
          {label}
        </label>
        {toolbar}
      </div>
      <div
        className={cn(
          'hue-input relative overflow-hidden rounded-xl border border-surface-border bg-surface-elevated focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20',
          className,
        )}
      >
        <div
          ref={highlight}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <pre className={cn(SHARED_TEXT, 'text-content')}>
            {paint(value, errorLines)}
            {/* A linha em branco no fim mantém a pintura alinhada quando o texto termina com
                quebra: sem ela, a última linha digitada fica um degrau acima do cursor. */}
            {'\n'}
          </pre>
        </div>
        <textarea
          id={id}
          value={value}
          onChange={(event) => {
            onValueChange(event.target.value);
          }}
          onScroll={(event) => {
            if (highlight.current) highlight.current.scrollTop = event.currentTarget.scrollTop;
          }}
          spellCheck={false}
          readOnly={readOnly}
          rows={rows}
          aria-describedby={hintId}
          className={cn(
            SHARED_TEXT,
            'relative resize-y bg-transparent text-transparent caret-brand-400 outline-none',
            readOnly && 'cursor-default',
          )}
        />
      </div>
      {hint ? (
        <p id={hintId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/// A pintura, linha a linha. Linha com erro ganha fundo próprio inteiro, e não só um ponto no
/// começo: o objetivo é a pessoa achar o lugar sem procurar.
function paint(value: string, errorLines: readonly number[]): ReactNode[] {
  const broken = new Set(errorLines);
  return value.split('\n').map((line, index) => (
    <span
      // A chave é a posição porque linha é posição: a terceira linha continua sendo a terceira
      // depois de editada, e recriá-la a cada tecla desperdiçaria o trabalho do React.
      key={index}
      className={cn('block', broken.has(index + 1) && 'bg-danger/15')}
    >
      {tokenize(line)}
      {'\n'}
    </span>
  ));
}

const TOKEN =
  /(\{\{\s*[A-Za-z][A-Za-z0-9_]*\s*\}\})|(<\/?[A-Za-z][A-Za-z0-9]*)|("[^"]*")|([A-Za-z-]+(?==))|(\/?>)/gu;

function tokenize(line: string): ReactNode[] {
  const pieces: ReactNode[] = [];
  let cursor = 0;
  for (const match of line.matchAll(TOKEN)) {
    const start = match.index;
    if (start > cursor) pieces.push(line.slice(cursor, start));
    pieces.push(
      <span key={start} className={colorOf(match)}>
        {match[0]}
      </span>,
    );
    cursor = start + match[0].length;
  }
  if (cursor < line.length) pieces.push(line.slice(cursor));
  return pieces;
}

function colorOf(match: RegExpMatchArray): string {
  if (match[1] !== undefined) return 'rounded bg-brand-500/20 font-semibold text-brand-300';
  if (match[2] !== undefined || match[5] !== undefined) return 'text-brand-400';
  if (match[3] !== undefined) return 'text-positive';
  return 'text-content-muted';
}
